'use client'

import * as LabelPrimitive from '@radix-ui/react-label'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { cva, VariantProps } from 'class-variance-authority'
import { ComponentProps } from 'react'

import { cn } from '@/shared/lib'

const ORIENTATION_VERTICAL = 'vertical'

const getOrientationClass = (orientation: RootProps['orientation']) => {
  return orientation === ORIENTATION_VERTICAL ? 'flex flex-col' : 'flex'
}

const getColorClass = (color: RootProps['color']) => {
  return color === 'primary'
    ? '[--radio-color:var(--color-black)] [--ring-color:var(--color-gray-200)]'
    : '[--radio-color:var(--color-purple-500)] [--ring-color:var(--color-purple-200)]'
}

const getSizeClass = (size: RootProps['size']) => {
  return size === 'medium'
    ? 'typo-body1 [--radio-size:--spacing(5)] [--radio-inner-size:--spacing(2)]'
    : 'typo-label1 [--radio-size:--spacing(4)] [--radio-inner-size:--spacing(1.5)]'
}

const getWeightClass = (weight: RootProps['weight']) => {
  const weightMap = {
    bold: 'font-bold',
    medium: 'font-medium',
    normal: 'font-normal',
  }

  return weightMap[weight ?? 'normal']
}

type RootProps = Omit<
  ComponentProps<typeof RadioGroupPrimitive.Root>,
  'onChange'
> & {
  color?: 'primary' | 'secondary'
  size?: 'small' | 'medium'
  weight?: 'normal' | 'medium' | 'bold'
  onChange?: (value: string) => void
}

export const Root = ({
  color = 'primary',
  size = 'medium',
  weight = 'normal',
  disabled = false,
  required = false,
  orientation = 'vertical',
  loop = true,
  asChild = false,
  className,
  onValueChange,
  onChange,
  ...props
}: RootProps) => {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn(
        'grid gap-1',
        getOrientationClass(orientation),
        getColorClass(color),
        getSizeClass(size),
        getWeightClass(weight),
        className,
      )}
      disabled={disabled}
      required={required}
      loop={loop}
      asChild={asChild}
      onValueChange={onValueChange ?? onChange}
      {...props}
    />
  )
}

export const Item = ({
  className,
  ...props
}: ComponentProps<typeof RadioGroupPrimitive.Item>) => {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        'text-primary m-0.5 aspect-square shrink-0 cursor-pointer rounded-full border border-gray-300 bg-white transition-[box-shadow] outline-none',
        'size-[var(--radio-size)]',
        'hover:bg-gray-100/30',
        'data-[state=checked]:border-[var(--radio-color)] data-[state=checked]:bg-[var(--radio-color)]',
        'data-[state=checked]:focus-visible:border-[var(--radio-color)]',
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        'focus-visible:ring-3 focus-visible:ring-[var(--ring-color)]/50',
        'disabled:cursor-default disabled:bg-gray-100 disabled:opacity-50',
        'peer/radio',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <div
          className={cn(
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white',
            'size-[var(--radio-inner-size)]',
          )}
        />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
}

type GroupProps = ComponentProps<'div'>

export const Group = ({ children, className }: GroupProps) => {
  return <div className={cn('flex space-x-[6px]', className)}>{children}</div>
}

const labelVariants = cva(
  [
    'flex w-fit cursor-pointer gap-1 text-black',
    'has-data-[disabled]:cursor-default',
  ],
  {
    variants: {
      weight: {
        normal: 'font-normal',
        medium: 'font-medium',
        bold: 'font-bold',
      },
    },
  },
)

type LabelProps = ComponentProps<typeof LabelPrimitive.Root> &
  VariantProps<typeof labelVariants>

export const Label = ({ className, weight, ...props }: LabelProps) => {
  return (
    <LabelPrimitive.Root
      className={cn(labelVariants({ weight, className }))}
      {...props}
    />
  )
}
